import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { isAdmin } = useAuth();
  const [form, setForm] = useState({ app_name: "", company_name: "", project_name: "", logo: null, public_url: "", purpose_options: [], overstay_hours: 8, test_mode: false });
  const [newPurpose, setNewPurpose] = useState("");
  const [clearing, setClearing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const s = r.data || {};
      setForm({ app_name: s.app_name || "Project GPI", company_name: s.company_name || "BSP MACHINES & TOOLS PVT. LTD.", project_name: s.project_name || "GPI PROJECT", logo: s.logo || null, public_url: s.public_url || window.location.origin, purpose_options: s.purpose_options || [], overstay_hours: s.overstay_hours ?? 8, test_mode: !!s.test_mode });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (!isAdmin) return (
    <div className="max-w-md mx-auto mt-16 text-center" data-testid="restricted-notice">
      <Icons.Lock className="h-8 w-8 mx-auto text-destructive mb-3" />
      <h2 className="font-display text-xl font-bold">Administrator Only</h2>
      <p className="text-muted-foreground text-sm mt-2">Only Admins can manage settings.</p>
    </div>
  );

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo", { storage_path: data.storage_path, original_filename: data.original_filename, content_type: data.content_type });
      toast.success("Logo uploaded — click Save to apply");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", form); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const toggleTestMode = async (v) => {
    set("test_mode", v);
    try { await api.put("/settings", { ...form, test_mode: v }); toast.success(v ? "Test Mode ON — new Gate Pass & Security entries are marked as test data" : "Test Mode OFF — entries are now production"); }
    catch (e) { set("test_mode", !v); toast.error(formatApiError(e.response?.data?.detail) || "Could not update Test Mode"); }
  };

  const clearTestData = async () => {
    if (!window.confirm("Permanently delete ALL test Gate Pass and Security entries? Production records are not affected.")) return;
    setClearing(true);
    try { const { data } = await api.post("/admin/test-data/clear"); toast.success(`Cleared ${data.gate_passes} gate pass & ${data.security} security test records`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Clear failed"); }
    finally { setClearing(false); }
  };

  if (loading) return <div className="flex justify-center mt-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <div className="overline text-muted-foreground mb-1">Administration</div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">Company branding used across the app and printed gate passes.</p>
      </div>

      <Card className="p-5 space-y-4">
        <div className="space-y-1.5">
          <Label className="overline text-muted-foreground">Application Name</Label>
          <Input value={form.app_name} onChange={(e) => set("app_name", e.target.value)} data-testid="settings-app-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-muted-foreground">Company Name (on Gate Pass)</Label>
          <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="settings-company-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-muted-foreground">Project Name (on Gate Pass)</Label>
          <Input value={form.project_name} onChange={(e) => set("project_name", e.target.value)} data-testid="settings-project-name" />
        </div>

        <div className="space-y-1.5">
          <Label className="overline text-muted-foreground">Public App URL (for Gate Pass QR codes)</Label>
          <Input value={form.public_url} onChange={(e) => set("public_url", e.target.value)} placeholder="https://your-app-url" data-testid="settings-public-url" />
          <p className="text-xs text-muted-foreground">Used to build the scannable QR on each gate pass so any phone camera opens the verify page. Auto-filled with this app's address.</p>
        </div>

        <div className="space-y-1.5">
          <Label className="overline text-muted-foreground">Company Logo (navy PNG recommended)</Label>
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-input bg-secondary text-sm cursor-pointer hover:bg-accent transition-colors" data-testid="settings-logo-upload">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {form.logo ? "Replace logo" : "Upload logo"}
              <input type="file" className="hidden" accept=".png,.jpg,.jpeg,.webp" onChange={handleLogo} disabled={uploading} />
            </label>
            {form.logo?.storage_path && (
              <div className="h-16 w-16 rounded-lg border border-border bg-white flex items-center justify-center overflow-hidden">
                <img src={fileUrl(form.logo.storage_path)} alt="logo" className="max-h-full max-w-full object-contain" data-testid="settings-logo-preview" />
              </div>
            )}
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="active:scale-95 transition-transform" data-testid="settings-save-btn">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save Settings
        </Button>
      </Card>

      <Card className="p-5 space-y-4">
        <div>
          <div className="overline text-muted-foreground mb-1">Security Entry & Exit</div>
          <h2 className="font-display text-lg font-bold">Gate Portal Settings</h2>
          <p className="text-xs text-muted-foreground">Manage the "Purpose of visit" options and the overstay alert threshold.</p>
        </div>

        <div className="space-y-2">
          <Label className="overline text-muted-foreground">Purpose of Visit options</Label>
          <div className="flex flex-wrap gap-2" data-testid="settings-purpose-list">
            {form.purpose_options.length === 0 && <span className="text-xs text-muted-foreground">No options yet — add some below.</span>}
            {form.purpose_options.map((p, i) => (
              <span key={p} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-sm" data-testid={`settings-purpose-chip-${i}`}>
                {p}
                <button type="button" onClick={() => set("purpose_options", form.purpose_options.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive" data-testid={`settings-purpose-remove-${i}`}><Icons.X className="h-3.5 w-3.5" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newPurpose} onChange={(e) => setNewPurpose(e.target.value)} placeholder="Add a purpose (e.g. Delivery)" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); const v = newPurpose.trim(); if (v && !form.purpose_options.includes(v)) set("purpose_options", [...form.purpose_options, v]); setNewPurpose(""); } }} data-testid="settings-purpose-input" />
            <Button type="button" variant="outline" onClick={() => { const v = newPurpose.trim(); if (v && !form.purpose_options.includes(v)) set("purpose_options", [...form.purpose_options, v]); setNewPurpose(""); }} data-testid="settings-purpose-add">Add</Button>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="overline text-muted-foreground">Overstay alert after (hours)</Label>
          <Input type="number" min="1" value={form.overstay_hours} onChange={(e) => set("overstay_hours", Number(e.target.value))} className="max-w-[160px]" data-testid="settings-overstay-hours" />
          <p className="text-xs text-muted-foreground">People/vehicles still inside beyond this many hours are flagged on the Security dashboard.</p>
        </div>

        <Button onClick={save} disabled={saving} className="active:scale-95 transition-transform" data-testid="settings-security-save-btn">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save Settings
        </Button>
      </Card>

      <Card className="p-5 space-y-4 border-amber-300/60">
        <div>
          <div className="overline text-muted-foreground mb-1">Super Admin</div>
          <h2 className="font-display text-lg font-bold">Test Mode</h2>
          <p className="text-xs text-muted-foreground">When ON, every new Gate Pass and Security entry is flagged as <b>test data</b> and kept out of dashboard counts. Use it to trial the workflow, then clear the test data in one click. Production records are never touched.</p>
        </div>
        <div className="flex items-center justify-between rounded-lg border px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Icons.FlaskConical className={`h-4 w-4 ${form.test_mode ? "text-amber-600" : "text-muted-foreground"}`} />
            Test Mode {form.test_mode ? "is ON" : "is OFF"}
          </div>
          <Switch checked={form.test_mode} onCheckedChange={toggleTestMode} data-testid="settings-test-mode-toggle" />
        </div>
        {form.test_mode && (
          <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2" data-testid="settings-test-mode-banner">
            <Icons.AlertTriangle className="h-4 w-4 shrink-0" />New Gate Pass & Security records are currently being saved as TEST data.
          </div>
        )}
        <Button variant="destructive" onClick={clearTestData} disabled={clearing} className="gap-1.5" data-testid="settings-clear-test-btn">
          {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Trash2 className="h-4 w-4" />}Clear All Test Data
        </Button>
      </Card>
    </div>
  );
}
